/**
 * Push Notification Settings Component
 * Allows users to enable/disable browser push notifications
 */
import React from 'react';
import { Bell, BellOff, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Alert, AlertDescription } from './ui/alert';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useToast } from '../hooks/use-toast';

export const PushNotificationSettings = ({ compact = false }) => {
  const { 
    isSupported, 
    isSubscribed, 
    permission, 
    loading, 
    error, 
    subscribe, 
    unsubscribe 
  } = usePushNotifications();
  const { toast } = useToast();

  const handleToggle = async () => {
    if (isSubscribed) {
      const success = await unsubscribe();
      if (success) {
        toast({
          title: "Push notificaties uitgeschakeld",
          description: "Je ontvangt geen browser meldingen meer.",
        });
      }
    } else {
      const success = await subscribe();
      if (success) {
        toast({
          title: "Push notificaties ingeschakeld!",
          description: "Je ontvangt een melding wanneer race resultaten beschikbaar zijn.",
        });
      } else if (permission === 'denied') {
        toast({
          title: "Notificaties geblokkeerd",
          description: "Je browser blokkeert notificaties. Wijzig dit in je browser instellingen.",
          variant: "destructive"
        });
      }
    }
  };

  // Not supported - show nothing or minimal message
  if (!isSupported) {
    if (compact) return null;
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Push notificaties worden niet ondersteund in deze browser.
        </AlertDescription>
      </Alert>
    );
  }

  // Compact version for settings page
  if (compact) {
    return (
      <div className="flex items-center justify-between py-2" data-testid="push-notification-toggle">
        <div className="flex items-center gap-3">
          {isSubscribed ? (
            <Bell className="h-5 w-5 text-green-500" />
          ) : (
            <BellOff className="h-5 w-5 text-gray-400" />
          )}
          <div>
            <p className="font-medium text-sm">Push Notificaties</p>
            <p className="text-xs text-gray-500">
              {isSubscribed ? 'Ingeschakeld' : 'Uitgeschakeld'}
            </p>
          </div>
        </div>
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Switch
            checked={isSubscribed}
            onCheckedChange={handleToggle}
            disabled={permission === 'denied'}
          />
        )}
      </div>
    );
  }

  // Full card version
  return (
    <Card data-testid="push-notification-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Bell className="h-5 w-5 text-[#E10600]" />
          Push Notificaties
        </CardTitle>
        <CardDescription>
          Ontvang een browser melding wanneer race resultaten beschikbaar zijn
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {permission === 'denied' && (
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Notificaties zijn geblokkeerd in je browser. 
              Klik op het slot-icoon in de adresbalk om dit te wijzigen.
            </AlertDescription>
          </Alert>
        )}
        
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">
              {isSubscribed ? 'Notificaties zijn ingeschakeld' : 'Notificaties zijn uitgeschakeld'}
            </p>
            <p className="text-sm text-gray-500">
              {isSubscribed 
                ? 'Je ontvangt een melding bij nieuwe race resultaten' 
                : 'Schakel in om meldingen te ontvangen'}
            </p>
          </div>
          
          <Button
            variant={isSubscribed ? "outline" : "default"}
            onClick={handleToggle}
            disabled={loading || permission === 'denied'}
            data-testid="push-toggle-btn"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : isSubscribed ? (
              <BellOff className="h-4 w-4 mr-2" />
            ) : (
              <Bell className="h-4 w-4 mr-2" />
            )}
            {isSubscribed ? 'Uitschakelen' : 'Inschakelen'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PushNotificationSettings;
